import React, { useState } from 'react';
import { Tag, Space, Input, Select, Row, Col } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useStaffs } from '../../hooks/useStaffs';
import { DataTable } from '../../components';
import type { Staff } from '../../services/staff.service';

// EG-52: Platform Admin xem nhân viên cross-tenant.
// Filter: tenant_id (input), q (search), state (active/inactive).
const StaffList: React.FC = () => {
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState('');
  const [stateFilter, setStateFilter] = useState<number | undefined>();
  const [tenantFilter, setTenantFilter] = useState<string>('');

  const { staffs, loading, meta, setFilters, refetch, filters } = useStaffs();

  const applyFilters = (overrides: Partial<typeof filters> = {}) => {
    setFilters({
      ...filters,
      page: 1,
      q: searchText || undefined,
      state: stateFilter,
      tenant_id: tenantFilter || undefined,
      ...overrides,
    });
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    applyFilters({ q: value || undefined });
  };

  const handlePageChange = (page: number, pageSize: number) => {
    setFilters({ ...filters, page, limit: pageSize });
  };

  const columns: ColumnsType<Staff> = [
    {
      title: 'Họ tên',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space direction="vertical" size={0}>
          <a onClick={() => navigate(`/staffs/${record.staff_id}`)}>{text}</a>
          <span style={{ fontSize: 12, color: '#8c8c8c' }}>@{record.username}</span>
        </Space>
      ),
    },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    { title: 'SĐT', dataIndex: 'phone', key: 'phone', width: 140 },
    {
      title: 'Tenant',
      dataIndex: 'tenant_id',
      key: 'tenant_id',
      width: 220,
      render: (id?: string) => id ? <code style={{ fontSize: 11 }}>{id.slice(-8)}</code> : '—',
    },
    {
      title: 'Trạng thái',
      dataIndex: 'state',
      key: 'state',
      width: 110,
      render: (state: number) => (
        <Tag color={state === 1 ? 'success' : 'default'}>
          {state === 1 ? 'Hoạt động' : 'Tạm khoá'}
        </Tag>
      ),
    },
    {
      title: '2FA',
      dataIndex: 'two_fa_enabled',
      key: 'two_fa_enabled',
      width: 80,
      render: (enabled?: boolean) => enabled ? <Tag color="blue">ON</Tag> : <Tag>OFF</Tag>,
    },
    {
      title: 'Ngày tạo',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 130,
      render: (date: string) => date ? dayjs(date).format('DD/MM/YYYY') : '—',
    },
  ];

  const filterContent = (
    <Row gutter={16}>
      <Col span={8}>
        <Input.Search
          placeholder="Tenant ID (tùy chọn)"
          allowClear
          value={tenantFilter}
          onChange={(e) => setTenantFilter(e.target.value)}
          onSearch={() => applyFilters()}
        />
      </Col>
      <Col span={6}>
        <Select
          placeholder="Trạng thái"
          allowClear
          style={{ width: '100%' }}
          value={stateFilter}
          onChange={(v) => { setStateFilter(v); applyFilters({ state: v }); }}
          options={[
            { value: 1, label: 'Hoạt động' },
            { value: 0, label: 'Tạm khoá' },
          ]}
        />
      </Col>
    </Row>
  );

  return (
    <DataTable<Staff>
      title="Nhân viên (cross-tenant)"
      data={staffs}
      columns={columns}
      loading={loading}
      rowKey="staff_id"
      searchable
      searchPlaceholder="Tên / username / email..."
      onSearch={handleSearch}
      onRefresh={refetch}
      filters={filterContent}
      pagination={{
        current: meta.page,
        pageSize: meta.limit,
        total: meta.total,
        onChange: handlePageChange,
      }}
      onRowClick={(record) => navigate(`/staffs/${record.staff_id}`)}
      actionColumn={{
        width: 80,
        fixed: 'right',
        actions: (record) => [
          {
            key: 'view',
            label: 'Xem chi tiết',
            icon: <EyeOutlined />,
            onClick: () => navigate(`/staffs/${record.staff_id}`),
          },
        ],
      }}
    />
  );
};

export default StaffList;
export { StaffList };
